import { Gauge, Leaf } from "lucide-react";
import { lazy } from "react";

import { POINT_KINDS } from "@/components/market-intelligence/PointKind";
import { POINT_GROUPS } from "@/lib/market-intelligence/point-groups";

import type { RegionTab } from "@/lib/market-intelligence/paths";
import type { IsoCode } from "@/types/market-intelligence";
import type { LucideIcon } from "lucide-react";
import type { ReactElement } from "react";

/**
 * The surfaces a region has.
 *
 * One registry, two consumers: the module builds a route per ISO per surface
 * from it, and the sidebar builds the scoped rows from it. They were separate
 * once, and the sidebar advertised a RECs link for MISO that led to a tab the
 * page had already dropped — so the rule for WHICH surfaces a region has
 * lives in `regionHasSurface`, and the list of what surfaces exist at all
 * lives here.
 *
 * These were tabs on a single region page. The sidebar already lists them per
 * region, so a strip repeating them underneath gave the reader two controls
 * for one choice; the sidebar won because it stays visible while you drill
 * into a hub.
 *
 * Declared here rather than in the module file because the pages import it
 * too — putting it in the route table would be a circular import.
 */
const RegionHubsPage = lazy(() => import("./RegionHubsPage"));
const RegionRecsPage = lazy(() => import("./RegionRecsPage"));
const RegionCapacityPage = lazy(() => import("./RegionCapacityPage"));

export interface RegionSurface {
  /** The url segment, and the key `regionHasSurface` is written against. */
  path: RegionTab;
  label: string;
  icon: LucideIcon;
  /**
   * Builds the element for one ISO.
   *
   * The ISO is a prop rather than a route param: these are static routes, one
   * per region, and a static route carries no param. Reading `:iso` instead
   * is the bug that once rendered "Unknown region" for all seven.
   */
  render: (iso: IsoCode) => ReactElement;
}

export const REGION_SURFACES: ReadonlyArray<RegionSurface> = [
  // The region's pricing points, one row per kind (2026-10-01): hubs and load
  // zones are different things to a buyer, so the sidebar lists them apart,
  // each expanding into its own points. Both land on the region overview,
  // filtered to that kind; the bare region url shows it unfiltered.
  {
    path: "hubs",
    label: "Hubs",
    icon: POINT_KINDS.hub.icon,
    render: (iso) => <RegionHubsPage iso={iso} show="hub" />,
  },
  {
    path: "load-zones",
    label: "Load zones",
    icon: POINT_KINDS.load_zone.icon,
    render: (iso) => <RegionHubsPage iso={iso} show="load_zone" />,
  },
  {
    path: "recs",
    label: "RECs",
    icon: Leaf,
    render: (iso) => <RegionRecsPage iso={iso} />,
  },
  {
    path: "capacity",
    label: "Capacity",
    icon: Gauge,
    render: (iso) => <RegionCapacityPage iso={iso} />,
  },
];

/**
 * The region overview, unfiltered: every pricing point, hubs and load zones
 * together. Served at the bare region url (`/regions/pjm`), where the region
 * picker, the breadcrumb and the Overview's region cards all land. Not a
 * sidebar row: the Hubs and Load zones rows are this page filtered.
 */
export const REGION_OVERVIEW = {
  render: (iso: IsoCode): ReactElement => <RegionHubsPage iso={iso} show="all" />,
};

/**
 * A gas region's surfaces: `REGION_SURFACES`' counterpart, read by the same
 * two consumers (the module's routes and the scoped sidebar rows).
 *
 * Hubs and Delivery points — gas's two kinds of point, the counterparts of
 * power's hubs and load zones (`POINT_GROUPS`). No RECs and no Capacity: those
 * are power markets.
 *
 * `render` takes no region: gas routes are parameterised (`/gas/regions/
 * :region`), because gas regions come from the feed rather than a constant the
 * routes could be built from, and the page reads the param itself.
 */
export interface GasRegionSurface {
  path: RegionTab;
  label: string;
  icon: LucideIcon;
  render: () => ReactElement;
}

export const GAS_REGION_SURFACES: ReadonlyArray<GasRegionSurface> = POINT_GROUPS.gas.map(
  (group) => ({
    path: group.tab,
    label: group.label,
    icon: POINT_KINDS[group.kind].icon,
    render: () => <RegionHubsPage commodity="gas" show={group.kind} />,
  }),
);

/** A gas region's overview, unfiltered — `REGION_OVERVIEW` for gas. */
export const GAS_REGION_OVERVIEW = {
  render: (): ReactElement => <RegionHubsPage commodity="gas" show="all" />,
};
